import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Radio, Copy, RefreshCw, Eye, EyeOff, Settings2, Send, Megaphone,
  Signal, MapPin, Video, CircleDot, Save, Loader2, Calendar, HardDriveDownload, Power,
  Image as ImageIcon, Youtube, Facebook, Twitter, Plus, Trash2, Link2, ServerCog, Tv,
  Terminal, Play, Pencil, Info, CheckCircle2, Wifi, WifiOff, CircleSlash, ExternalLink, Zap, Code2,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import HlsPlayer from "@/components/HlsPlayer";
import { ChatModeration } from "@/components/ChatModeration";
import { DestinationPicker } from "@/components/DestinationPicker";
import { RestreamConnect } from "@/components/RestreamConnect";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DestinationTestResult } from "@/components/DestinationTestResult";
import { ChatSettingsCard } from "@/components/ChatSettingsCard";
import { PrayerRequests } from "@/components/PrayerRequests";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PLATFORMS = {
  youtube: { label: "YouTube", icon: Youtube, color: "text-red-400" },
  facebook: { label: "Facebook", icon: Facebook, color: "text-blue-400" },
  x: { label: "X (Twitter)", icon: Twitter, color: "text-sky-400" },
  rumble: { label: "Rumble", icon: Play, color: "text-[#85C742]" },
  custom: { label: "Custom RTMP", icon: Tv, color: "text-amber-400" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const WEEKDAYS = [
  { v: 0, label: "Sunday" }, { v: 1, label: "Monday" }, { v: 2, label: "Tuesday" },
  { v: 3, label: "Wednesday" }, { v: 4, label: "Thursday" }, { v: 5, label: "Friday" }, { v: 6, label: "Saturday" },
];
const COMMON_TZS = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu",
  "America/Toronto", "Europe/London", "Europe/Paris", "Australia/Sydney", "UTC",
];


// ISO (UTC) -> value for <input type="datetime-local"> in the user's local time.
function isoToLocalInput(iso) {
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
  } catch { return ""; }
}


function StatusRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</p>
        <div className="text-sm text-gray-200 mt-0.5">{children}</div>
      </div>
    </div>
  );
}

export default function StreamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({ title: "", description: "" });
  const [thumbInput, setThumbInput] = useState("");
  const [nextServiceInput, setNextServiceInput] = useState("");
  const [serviceTz, setServiceTz] = useState("");
  const [serviceSlots, setServiceSlots] = useState([]); // [{weekday, time}]
  const [savingThumb, setSavingThumb] = useState(false);
  const [destOpen, setDestOpen] = useState(false);
  const [encoderOpen, setEncoderOpen] = useState(false);
  const [ingest, setIngest] = useState(null);
  const [provisioning, setProvisioning] = useState(false);
  const [viewers, setViewers] = useState(0);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedSize, setEmbedSize] = useState("640");
  const [customBar, setCustomBar] = useState(false);
  const [barColor, setBarColor] = useState("#3B82F6");
  const [barTextColor, setBarTextColor] = useState("#FFFFFF");
  const [planLimits, setPlanLimits] = useState(null);
  const [embedChat, setEmbedChat] = useState(true);
  const [destToDelete, setDestToDelete] = useState(null);
  const [destToEdit, setDestToEdit] = useState(null);

  const withChat = !!stream?.chat_enabled && embedChat;
  const qs = [
    customBar ? `accent=${barColor.replace("#", "")}&text=${barTextColor.replace("#", "")}` : "",
    stream?.chat_enabled && !embedChat ? "chat=0" : "",
  ].filter(Boolean).join("&");
  const embedSrc = `${window.location.origin}/embed/live/${id}${qs ? `?${qs}` : ""}`;
  const embedMaxWidth = embedSize === "full" ? "100%" : `${embedSize}px`;
  const embedCode = `<div style="position:relative;width:100%;max-width:${embedMaxWidth};margin:0 auto;">
  <div style="position:relative;padding-top:56.25%;${withChat ? "min-height:520px;box-sizing:border-box;" : ""}">
    <iframe src="${embedSrc}" allow="autoplay; fullscreen; encrypted-media" allowfullscreen frameborder="0" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;border-radius:12px;"></iframe>
  </div>
</div>`;

  const load = async () => {
    try {
      const res = await api.get(`/streams/${id}`);
      setStream(res.data);
      setEdit({ title: res.data.title, description: res.data.description });
      setThumbInput(res.data.thumbnail_url || "");
      setNextServiceInput(res.data.next_service_at ? isoToLocalInput(res.data.next_service_at) : "");
      setServiceTz(res.data.service_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
      setServiceSlots(res.data.service_schedule || []);
      try {
        const ing = await api.get(`/streams/${id}/ingest`);
        setIngest(ing.data);
      } catch (e) { /* ingest info optional */ }
    } catch (e) {
      toast.error("Stream not found");
      navigate("/streams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/me/plan").then((r) => setPlanLimits(r.data)).catch(() => {});
    // Poll live status + playback URL so the page reflects going live/offline
    // (and picks up the fresh per-broadcast playback URL) without a manual refresh.
    const t = setInterval(async () => {
      try {
        const [s, ing] = await Promise.all([
          api.get(`/streams/${id}`),
          api.get(`/streams/${id}/ingest`).catch(() => null),
        ]);
        setStream(s.data);
        if (ing) setIngest(ing.data);
      } catch (e) { /* transient */ }
    }, 15000);
    return () => clearInterval(t);
  }, [id]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  // Live viewer counter: while this player is watching a live stream, send a
  // heartbeat every 15s so the backend can count concurrent viewers.
  const isWatchingLive = stream?.status === "active" && !!ingest?.hls_playback_url;
  useEffect(() => {
    if (!isWatchingLive) { setViewers(0); return; }
    let sid = sessionStorage.getItem("sw_viewer_sid");
    if (!sid) { sid = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2)); sessionStorage.setItem("sw_viewer_sid", sid); }
    let stop = false;
    const beat = async () => {
      try {
        const res = await api.post(`/streams/${id}/heartbeat`, { session_id: sid });
        if (!stop) setViewers(res.data?.viewers ?? 0);
      } catch (e) { /* transient */ }
    };
    beat();
    const t = setInterval(beat, 15000);
    return () => { stop = true; clearInterval(t); };
  }, [id, isWatchingLive]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post(`/streams/${id}/regenerate-key`);
      setStream(res.data);
      setShowKey(true);
      toast.success("Stream key regenerated — old key is now invalid");
    } catch (e) {
      toast.error("Failed to regenerate key");
    } finally {
      setRegenerating(false);
      setRegenOpen(false);
    }
  };

  const simulateIngest = async () => {
    const event = stream.status === "active" ? "publish_done" : "publish";
    try {
      // Hits the SAME webhook a real RTMP media server calls (matched by stream key)
      await api.post(`/streams/webhook/ingest`, { event, name: stream.stream_key });
      await load();
      toast.success(event === "publish" ? "Encoder push detected — stream is LIVE" : "Encoder stopped — stream offline");
    } catch (e) {
      toast.error("Failed to simulate ingest");
    }
  };

  const provisionPlayback = async () => {
    setProvisioning(true);
    try {
      const res = await api.post(`/streams/${id}/restreamer/provision`);
      setIngest((prev) => ({ ...(prev || {}), hls_playback_url: res.data.hls_playback_url }));
      toast.success("Playback enabled on the Restreamer engine");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to enable playback");
    } finally {
      setProvisioning(false);
    }
  };

  const saveThumbnail = async () => {
    setSavingThumb(true);
    try {
      const next_service_at = nextServiceInput ? new Date(nextServiceInput).toISOString() : "";
      const service_schedule = serviceSlots.filter((s) => s.time).map((s) => ({ weekday: Number(s.weekday), time: s.time, label: (s.label || "").trim() }));
      const res = await api.put(`/streams/${id}/thumbnail`, { thumbnail_url: thumbInput, next_service_at, service_schedule, service_timezone: serviceTz });
      setStream(res.data);
      toast.success("Offline screen updated");
    } catch (e) {
      toast.error("Failed to save thumbnail");
    } finally {
      setSavingThumb(false);
    }
  };

  const toggleDestination = async (dest) => {
    try {
      const res = await api.put(`/streams/${id}/destinations/${dest.id}`, { enabled: !dest.enabled });
      setStream(res.data);
    } catch (e) {
      toast.error("Failed to update destination");
    }
  };

  const deleteDestination = async (dest) => {
    try {
      const res = await api.delete(`/streams/${id}/destinations/${dest.id}`);
      setStream(res.data);
      toast.success("Destination removed");
    } catch (e) {
      toast.error("Failed to remove destination");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/streams/${id}`, edit);
      setStream(res.data);
      toast.success("Stream settings saved");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !stream) {
    return (
      <div className="flex items-center justify-center py-40">
        <Loader2 className="h-6 w-6 text-amber-400 animate-spin" />
      </div>
    );
  }

  const active = stream.status === "active";
  const effectiveRtmp = ingest?.rtmp_server || stream.rtmp_url;
  const effectiveKey = ingest?.stream_key_full || stream.stream_key;
  const maskedKey = showKey ? effectiveKey : effectiveKey.replace(/./g, (c, i) => (i < 14 ? c : "•"));
  const webhookUrl = `${API}/streams/webhook/ingest`;
  const nginxConfig = `rtmp {
    server {
        listen 1935;
        chunk_size 4096;

        application live {
            live on;
            record all;
            record_path /var/rec;

            # Auto-update StreamWorship stream status
            on_publish ${webhookUrl};
            on_publish_done ${webhookUrl};
        }
    }
}`;

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <button data-testid="back-to-streams" onClick={() => navigate("/streams")} className="flex items-center gap-2 text-sm text-gray-400 hover:text-amber-400 transition-colors mb-5">
        <ArrowLeft className="h-4 w-4" /> Back to Streams
      </button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-8">
        <div>
          <h1 data-testid="stream-title" className="font-display text-3xl font-bold tracking-tight">{stream.title}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
            <span className="text-gray-500 font-mono text-xs">ID: {stream.id.slice(0, 12)}</span>
            <span className="text-gray-700">·</span>
            <span className={"inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border " + (active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-500/15 text-gray-400 border-gray-500/30")}>{active ? "ACTIVE" : "OFFLINE"}</span>
            <span className="text-gray-700">·</span>
            <span className="inline-flex items-center gap-1.5 text-xs">
              <span className={"h-2 w-2 rounded-full " + (active ? "bg-emerald-400 animate-live-dot" : "bg-gray-500")} />
              <span className={active ? "text-emerald-400" : "text-gray-400"}>{active ? "Live" : "Offline"}</span>
            </span>
            <span className="text-gray-700">·</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <Signal className="h-3 w-3" /> LIVE PUSH
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="encoder-setup-button" onClick={() => setEncoderOpen(true)} className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm bg-card border border-gray-800 text-gray-300 hover:border-amber-500/40 hover:text-white transition-colors">
            <Settings2 className="h-4 w-4" /> Encoder Setup
          </button>
          <button className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm bg-card border border-gray-800 text-gray-300 hover:border-amber-500/40 hover:text-white transition-colors">
            <Send className="h-4 w-4" /> Publish
          </button>
          <button data-testid="toggle-status-button" onClick={simulateIngest} className="gold-gradient text-black font-semibold flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm hover:brightness-110 transition-all active:scale-[0.98] cursor-pointer">
            <Power className="h-4 w-4" /> {active ? "Simulate End" : "Simulate Ingest"}
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Main */}
        <div className="lg:col-span-8 space-y-6">
          {/* Video preview — single player: shows the live HLS feed when live,
              or a compact offline placeholder otherwise. */}
          {active && ingest?.hls_playback_url ? (
            <div data-testid="live-player" className="bg-black rounded-2xl border border-gray-800 aspect-video relative overflow-hidden">
              <span className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-600 text-white pointer-events-none">
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-live-dot" /> LIVE
              </span>
              <span data-testid="viewer-count" className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-black/60 backdrop-blur text-white border border-white/15 pointer-events-none">
                <Eye className="h-3.5 w-3.5" /> {viewers} watching
              </span>
              <HlsPlayer src={ingest.hls_playback_url} autoPlay muted controls live className="h-full w-full" />
            </div>
          ) : active ? (
            <div data-testid="live-connecting" className="bg-black rounded-2xl border border-gray-800 aspect-video flex flex-col items-center justify-center text-center relative overflow-hidden bg-grid">
              <span className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-600 text-white">
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-live-dot" /> LIVE
              </span>
              <Loader2 className="h-10 w-10 text-amber-500/60 animate-spin relative z-10" />
              <p className="mt-4 text-base font-medium text-white relative z-10">Receiving signal — preparing playback…</p>
            </div>
          ) : (
            <div data-testid="offline-placeholder" className="bg-black rounded-2xl border border-gray-800 h-40 sm:h-48 flex items-center justify-center gap-4 px-6 text-left relative overflow-hidden bg-grid">
              {stream.thumbnail_url && (
                <img src={stream.thumbnail_url} alt={stream.title} className="absolute inset-0 h-full w-full object-cover opacity-30" />
              )}
              <div className="h-12 w-12 rounded-full border border-amber-500/20 flex items-center justify-center animate-pulse-gold relative z-10 bg-black/40 shrink-0">
                <Video className="h-6 w-6 text-amber-500/60" />
              </div>
              <div className="relative z-10">
                <p className="text-base font-semibold text-white">Broadcast is currently offline</p>
                <p className="text-sm text-gray-400 mt-0.5">Push your encoder to the RTMP URL below to go live.</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "overview"}>
            <TabsList className="bg-transparent border-b border-gray-800 rounded-none w-full justify-start h-auto p-0 gap-6">
              {["overview", "images", "destinations", "chat"].map((t) => (
                <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="capitalize rounded-none border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:text-amber-400 data-[state=active]:bg-transparent text-gray-400 px-1 pb-3 pt-0 font-medium">
                  {t}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overview" className="pt-6">
              <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Radio className="h-5 w-5 text-amber-400" /> Stream Settings</h3>

                {/* RTMP URL */}
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Server URL</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center gap-2 bg-gray-950 rounded-lg px-3 py-2.5 border border-gray-800 min-w-0">
                      <CircleDot className="h-4 w-4 text-amber-400 shrink-0" />
                      <code data-testid="rtmp-url-value" className="text-sm text-gray-300 truncate font-mono">{effectiveRtmp}</code>
                    </div>
                    <button data-testid="copy-rtmp-button" onClick={() => copy(effectiveRtmp, "Server URL")} className="px-3 rounded-lg bg-gray-950 border border-gray-800 text-gray-400 hover:text-amber-400 hover:border-amber-500/40 flex items-center gap-2 text-sm transition-colors">
                      <Copy className="h-4 w-4" /> Copy
                    </button>
                  </div>
                </div>

                {/* Stream Key */}
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Stream Key</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center bg-gray-950 rounded-lg px-3 py-2.5 border border-gray-800 min-w-0">
                      <code data-testid="stream-key-value" className="text-sm text-amber-300 truncate font-mono flex-1">{maskedKey}</code>
                      <button data-testid="toggle-key-visibility" onClick={() => setShowKey((s) => !s)} className="text-gray-500 hover:text-amber-400 ml-2 transition-colors">
                        {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <button data-testid="copy-key-button" onClick={() => copy(effectiveKey, "Stream Key")} className="px-3 rounded-lg bg-gray-950 border border-gray-800 text-gray-400 hover:text-amber-400 hover:border-amber-500/40 flex items-center gap-2 text-sm transition-colors">
                      <Copy className="h-4 w-4" /> Copy
                    </button>
                    <button data-testid="regenerate-key-button" onClick={() => setRegenOpen(true)} className="px-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 flex items-center gap-2 text-sm transition-colors">
                      <RefreshCw className="h-4 w-4" /> Regenerate
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Keep this key secret. Regenerating instantly invalidates the previous key.</p>
                  <div data-testid="token-note" className="flex items-start gap-2 rounded-lg border border-sky-500/25 bg-sky-500/[0.06] px-3 py-2.5">
                    <Info className="h-4 w-4 text-sky-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-gray-300 leading-relaxed">
                      Paste the <span className="text-white font-medium">Server URL</span> and <span className="text-white font-medium">Stream Key</span> into their own fields in OBS, Streamlabs, vMix or any encoder.
                      The security token is handled automatically — <span className="text-white font-medium">do not add <code className="font-mono">?token=</code> or anything else to the stream key.</span>
                    </p>
                  </div>
                </div>

                {ingest?.restreamer_enabled && (
                  <div data-testid="restreamer-card" className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-white flex items-center gap-2">
                        <Radio className="h-4 w-4 text-amber-400" /> Live Ingest — Restreamer engine
                      </p>
                      <span data-testid="restreamer-live-state" className={"inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full " + (ingest.is_live ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-gray-700/30 text-gray-400 border border-gray-700")}>
                        <span className={"h-1.5 w-1.5 rounded-full " + (ingest.is_live ? "bg-emerald-400" : "bg-gray-500")} /> {ingest.is_live ? "LIVE" : "Offline"}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-300 text-xs">Push URL for OBS / encoder</Label>
                      <div className="flex gap-2">
                        <code data-testid="restreamer-push-url" className="flex-1 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs font-mono text-gray-300 truncate">{ingest.push_url}</code>
                        <button data-testid="copy-push-url" onClick={() => copy(ingest.push_url, "Push URL")} className="h-9 w-9 rounded-lg bg-gray-950 border border-gray-800 text-gray-400 hover:text-amber-400 flex items-center justify-center"><Copy className="h-4 w-4" /></button>
                      </div>
                      <p className="text-[11px] text-gray-500">Only for encoders that take a single URL. OBS, Streamlabs and vMix should use the separate Server URL and Stream Key above.</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-xs">HLS playback URL</Label>
                      {ingest.hls_playback_url ? (
                        <div className="flex gap-2">
                          <code data-testid="restreamer-hls-url" className="flex-1 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs font-mono text-amber-300 truncate">{ingest.hls_playback_url}</code>
                          <button data-testid="copy-hls-url" onClick={() => copy(ingest.hls_playback_url, "HLS URL")} className="h-9 w-9 rounded-lg bg-gray-950 border border-gray-800 text-gray-400 hover:text-amber-400 flex items-center justify-center"><Copy className="h-4 w-4" /></button>
                        </div>
                      ) : (
                        <button data-testid="provision-playback" onClick={provisionPlayback} disabled={provisioning} className="text-sm gold-gradient text-black font-semibold px-3.5 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-60">
                          {provisioning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ServerCog className="h-4 w-4" />} Enable playback on Restreamer
                        </button>
                      )}
                    </div>

                    <div className="space-y-2 pt-1">
                      <Label className="text-gray-300 text-xs">Website embed (counts viewers)</Label>
                      <div className="flex items-center justify-between gap-2 rounded-lg bg-gray-950 border border-gray-800 px-3 py-2.5">
                        <p className="text-[11px] text-gray-400 leading-snug">
                          Paste a player on your website with an iframe. Everyone watching through it is counted in your live viewer total.
                          <span className="text-gray-500"> (Roku/Fire TV apps can't be counted.)</span>
                        </p>
                        <button data-testid="open-live-embed" onClick={() => setEmbedOpen(true)} className="shrink-0 text-xs gold-gradient text-black font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5">
                          <Code2 className="h-3.5 w-3.5" /> Embed code
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="h-px bg-gray-800" />

                {/* Editable fields */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Stream Name</Label>
                    <Input data-testid="edit-stream-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="text-gray-300 text-sm">Description</Label>
                    <Textarea data-testid="edit-stream-description" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={2} className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 resize-none" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button data-testid="save-stream-button" onClick={save} disabled={saving} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60 cursor-pointer">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
                  </button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="chat" className="pt-6">
              <div className="space-y-6">
                <ChatModeration stream={stream} onToggled={setStream} />
                <ChatSettingsCard streamId={stream.id} />
                <PrayerRequests streamId={stream.id} />
              </div>
            </TabsContent>

            <TabsContent value="images" className="pt-6">
              <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><ImageIcon className="h-5 w-5 text-amber-400" /> Offline screen</h3>
                <p className="text-sm text-gray-400">Paste an image URL to use as this stream's poster (shown while offline), and optionally a "next service" time that counts down over it.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input data-testid="thumbnail-url-input" value={thumbInput} onChange={(e) => setThumbInput(e.target.value)} placeholder="https://…/poster.jpg" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 flex-1" />
                  <button data-testid="save-thumbnail-button" onClick={saveThumbnail} disabled={savingThumb} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer">
                    {savingThumb ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                  </button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-gray-300 text-sm">Next service starts at <span className="text-gray-500 font-normal">(optional)</span></Label>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <Input data-testid="next-service-input" type="datetime-local" value={nextServiceInput} onChange={(e) => setNextServiceInput(e.target.value)} className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 sm:w-64 [color-scheme:dark]" />
                    {nextServiceInput && (
                      <button type="button" data-testid="clear-next-service" onClick={() => setNextServiceInput("")} className="text-xs text-gray-400 hover:text-white underline underline-offset-2 self-start sm:self-auto">Clear</button>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500">Shown as a live countdown over the offline poster in your website embed. Save to apply.</p>
                </div>

                {/* Recurring weekly services (multiple per week supported) */}
                <div className="space-y-2.5 rounded-xl border border-gray-800 bg-gray-950/60 p-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <Label className="text-gray-300 text-sm">Recurring weekly services</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-500">Timezone</span>
                      <Select value={serviceTz} onValueChange={setServiceTz}>
                        <SelectTrigger data-testid="service-timezone-select" className="h-8 w-48 bg-gray-950 border-gray-800 text-xs focus:ring-amber-500/40">
                          <SelectValue placeholder="Timezone" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-950 border-gray-800 text-white max-h-[260px]">
                          {[...new Set([serviceTz, ...COMMON_TZS].filter(Boolean))].map((tz) => (
                            <SelectItem key={tz} value={tz} className="focus:bg-amber-500/10 text-xs">{tz.replace("_", " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {serviceSlots.length === 0 && (
                    <p className="text-[11px] text-gray-500">No weekly services set. Add one so the countdown rolls to the next service automatically.</p>
                  )}

                  <div className="space-y-2">
                    {serviceSlots.map((slot, i) => (
                      <div key={i} data-testid={`service-slot-${i}`} className="flex items-center gap-2 flex-wrap">
                        <Select value={String(slot.weekday)} onValueChange={(v) => setServiceSlots((arr) => arr.map((s, idx) => idx === i ? { ...s, weekday: Number(v) } : s))}>
                          <SelectTrigger data-testid={`service-slot-day-${i}`} className="h-9 w-36 bg-gray-950 border-gray-800 text-sm focus:ring-amber-500/40">
                            <SelectValue placeholder="Day" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-950 border-gray-800 text-white">
                            {WEEKDAYS.map((d) => <SelectItem key={d.v} value={String(d.v)} className="focus:bg-amber-500/10">{d.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input data-testid={`service-slot-time-${i}`} type="time" value={slot.time || ""} onChange={(e) => setServiceSlots((arr) => arr.map((s, idx) => idx === i ? { ...s, time: e.target.value } : s))} className="h-9 w-28 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 [color-scheme:dark]" />
                        <Input data-testid={`service-slot-label-${i}`} value={slot.label || ""} maxLength={40} onChange={(e) => setServiceSlots((arr) => arr.map((s, idx) => idx === i ? { ...s, label: e.target.value } : s))} placeholder="Label (e.g. Sunday Worship)" className="h-9 flex-1 min-w-[160px] bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
                        <button type="button" data-testid={`service-slot-remove-${i}`} onClick={() => setServiceSlots((arr) => arr.filter((_, idx) => idx !== i))} className="h-9 w-9 shrink-0 rounded-lg border border-gray-800 text-gray-500 hover:text-red-400 hover:border-red-500/40 flex items-center justify-center transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button type="button" data-testid="add-service-slot" onClick={() => setServiceSlots((arr) => [...arr, { weekday: 0, time: "10:00", label: "" }])} className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 font-medium">
                    <Plus className="h-3.5 w-3.5" /> Add service
                  </button>
                  <p className="text-[11px] text-gray-500">The embed countdown always targets the next upcoming service across all the times above. Click Save to apply.</p>
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-800 bg-gray-950 aspect-video flex items-center justify-center">
                  {stream.thumbnail_url ? (
                    <img data-testid="thumbnail-preview" src={stream.thumbnail_url} alt="poster" className="h-full w-full object-cover" />
                  ) : (
                    <div className="text-center text-gray-600">
                      <ImageIcon className="h-8 w-8 mx-auto" />
                      <p className="text-sm mt-2">No poster set</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="destinations" className="pt-6">
              <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Send className="h-5 w-5 text-amber-400" /> Restream Destinations</h3>
                  <button data-testid="add-destination-button" onClick={() => setDestOpen(true)} className="gold-gradient text-black font-semibold px-3 py-2 rounded-lg flex items-center gap-2 text-sm cursor-pointer">
                    <Plus className="h-4 w-4" /> Add Destination
                  </button>
                </div>

                <RestreamConnect stream={stream} forwarding={ingest?.forwarding} onSaved={setStream} />

                {(() => {
                  const liveNow = ingest?.is_live;
                  const enabledCount = (stream.destinations || []).filter((d) => d.enabled).length;
                  const fwdActive = (ingest?.forwarding || []).filter((f) => f.active).length;
                  if (!ingest?.restreamer_enabled) {
                    return (
                      <div data-testid="forwarding-banner" className="flex items-start gap-3 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3">
                        <ServerCog className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-300">Forwarding pending media server</p>
                          <p className="text-xs text-gray-400 mt-0.5">Destinations are saved and ready. Live forwarding to these platforms begins once your RTMP media server is attached.</p>
                        </div>
                      </div>
                    );
                  }
                  if (liveNow) {
                    return (
                      <div data-testid="forwarding-banner" className="flex items-start gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3">
                        <Wifi className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-emerald-300">Forwarding active · {fwdActive}/{enabledCount} destination{enabledCount === 1 ? "" : "s"} live</p>
                          <p className="text-xs text-gray-400 mt-0.5">Your live signal is being re-streamed to every enabled destination below. It stops automatically when the broadcast ends.</p>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div data-testid="forwarding-banner" className="flex items-start gap-3 rounded-xl border border-gray-700 bg-gray-800/40 px-4 py-3">
                      <ServerCog className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-200">Forwarding ready</p>
                        <p className="text-xs text-gray-400 mt-0.5">Enabled destinations start receiving your signal automatically the moment this stream goes live.</p>
                      </div>
                    </div>
                  );
                })()}

                {(stream.destinations || []).length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-gray-800 rounded-xl text-gray-500">
                    <Send className="h-8 w-8 mx-auto text-amber-500/40" />
                    <p className="text-sm mt-3">No destinations yet. Add YouTube Live, Facebook Live, Rumble or a custom RTMP server to re-stream.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stream.destinations.map((d) => {
                      const p = PLATFORMS[d.platform] || PLATFORMS.custom;
                      const Icon = p.icon;
                      return (
                        <div key={d.id} data-testid={`destination-${d.id}`} className="rounded-xl border border-gray-800 bg-gray-950/60 p-4 space-y-3">
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-lg bg-gray-800 flex items-center justify-center shrink-0">
                            <Icon className={"h-5 w-5 " + p.color} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white truncate">{d.name}{!d.name.toLowerCase().startsWith(p.label.toLowerCase()) && <span className="text-gray-500 font-normal"> · {p.label}</span>}</p>
                            <p className="text-xs text-gray-500 truncate font-mono flex items-center gap-1"><Link2 className="h-3 w-3" /> {d.rtmp_url}</p>
                          </div>
                          {(() => {
                            const fwd = (ingest?.forwarding || []).find((f) => f.id === d.id);
                            const liveNow = ingest?.is_live;
                            const health = !d.enabled ? "disabled"
                              : (liveNow && fwd?.active) ? "forwarding"
                              : (liveNow) ? "connecting"
                              : "standby";
                            const map = {
                              forwarding: { cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", label: "Forwarding", dot: "bg-emerald-400 animate-live-dot" },
                              connecting: { cls: "text-amber-400 bg-amber-500/10 border-amber-500/30", label: "Connecting…", dot: "bg-amber-400 animate-pulse" },
                              standby: { cls: "text-gray-400 bg-gray-500/10 border-gray-500/30", label: "Standby", dot: "bg-gray-500" },
                              disabled: { cls: "text-gray-500 bg-gray-800 border-gray-700", label: "Disabled", dot: "bg-gray-600" },
                            }[health];
                            return (
                              <span data-testid={`destination-health-${d.id}`} className={"hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border " + map.cls}>
                                <span className={"h-1.5 w-1.5 rounded-full " + map.dot} /> {map.label}
                              </span>
                            );
                          })()}
                          <Switch data-testid={`destination-toggle-${d.id}`} checked={d.enabled} onCheckedChange={() => toggleDestination(d)} className="data-[state=checked]:bg-amber-500" />
                          <button data-testid={`destination-edit-${d.id}`} title="Edit" onClick={() => setDestToEdit(d)} className="h-8 w-8 rounded-lg text-gray-500 hover:text-amber-400 hover:bg-amber-500/10 flex items-center justify-center transition-colors">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button data-testid={`destination-delete-${d.id}`} onClick={() => setDestToDelete(d)} className="h-8 w-8 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="pl-14"><DestinationTestResult streamId={id} destId={d.id} compact /></div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Status panel */}
        <div className="lg:col-span-4">
          <div className="bg-card border border-gray-800 rounded-2xl p-5 space-y-5 sticky top-24">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 uppercase tracking-widest">Status</p>
              <span className={"inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border " + (active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-500/15 text-gray-400 border-gray-500/30")}>{active ? "ACTIVE" : "OFFLINE"}</span>
            </div>
            <div className="h-px bg-gray-800" />
            <StatusRow icon={Signal} label="Stream Type">
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">{stream.stream_type}</span>
            </StatusRow>
            <StatusRow icon={MapPin} label="Server Location">{stream.server_location}</StatusRow>
            <StatusRow icon={Video} label="Broadcast">
              <span className={active ? "text-emerald-400" : "text-gray-400"}>{active ? "Live · Receiving signal" : "Offline"}</span>
            </StatusRow>
            <StatusRow icon={HardDriveDownload} label="Recording">{stream.recording}</StatusRow>
            <StatusRow icon={Calendar} label="Created">{fmtDate(stream.created_at)}</StatusRow>
            <StatusRow icon={Calendar} label="Updated">{fmtDate(stream.updated_at)}</StatusRow>
          </div>
        </div>
      </div>

      {/* Regenerate confirm */}
      <AlertDialog open={regenOpen} onOpenChange={setRegenOpen}>
        <AlertDialogContent className="bg-card border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate stream key?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              The current stream key will be <span className="text-amber-400 font-medium">invalidated immediately</span>. Any encoder using the old key will disconnect and must be reconfigured.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-regenerate-key" onClick={(e) => { e.preventDefault(); regenerate(); }} disabled={regenerating} className="gold-gradient text-black font-semibold">
              {regenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DestinationPicker open={destOpen} onOpenChange={setDestOpen} streamId={id} onSaved={setStream} />
      <DestinationPicker open={!!destToEdit} onOpenChange={(v) => !v && setDestToEdit(null)} streamId={id} onSaved={setStream} editing={destToEdit} />
      <ConfirmDialog open={!!destToDelete} onOpenChange={(v) => !v && setDestToDelete(null)} title="Remove destination?"
        description={`${destToDelete?.name || "This destination"} will stop receiving your broadcast.`} confirmLabel="Remove"
        onConfirm={() => { deleteDestination(destToDelete); setDestToDelete(null); }} />

      {/* Encoder Setup guide */}
      <Dialog open={encoderOpen} onOpenChange={setEncoderOpen}>
        <DialogContent className="bg-card border-gray-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><Terminal className="h-5 w-5 text-amber-400" /> Encoder & Server Setup</DialogTitle>
            <DialogDescription className="text-gray-400">Point your encoder at this stream, and wire your media server's hooks to auto-update status.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-1">
            <a
              data-testid="encoder-ascend-badge"
              href="https://ascendbroadcastsolutions.com"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-4 py-3 hover:border-amber-500/50 transition-colors group"
            >
              <img src="/ascend-logo.png" alt="ASCEND Broadcast Studio" className="h-7 w-auto object-contain shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-amber-300">Recommended encoder — one-click push to StreamWorship</p>
                <p className="text-[11px] text-gray-400 mt-0.5 truncate">ASCEND runs cameras, scripture &amp; lyrics on one PC. Any RTMP encoder (OBS, vMix, Wirecast) works too.</p>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-500 group-hover:text-amber-400 shrink-0" />
            </a>

            <div>
              <p className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Video className="h-4 w-4 text-amber-400" /> OBS / Encoder settings</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-24 shrink-0">Server</span>
                  <code className="flex-1 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs font-mono text-gray-300 truncate">{stream.rtmp_url}</code>
                  <button data-testid="copy-encoder-server" onClick={() => copy(stream.rtmp_url, "Server URL")} className="h-8 w-8 rounded-lg bg-gray-950 border border-gray-800 text-gray-400 hover:text-amber-400 flex items-center justify-center"><Copy className="h-4 w-4" /></button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-24 shrink-0">Stream Key</span>
                  <code className="flex-1 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs font-mono text-amber-300 truncate">{stream.stream_key}</code>
                  <button data-testid="copy-encoder-key" onClick={() => copy(stream.stream_key, "Stream Key")} className="h-8 w-8 rounded-lg bg-gray-950 border border-gray-800 text-gray-400 hover:text-amber-400 flex items-center justify-center"><Copy className="h-4 w-4" /></button>
                </div>
              </div>
              <div data-testid="keyframe-tip" className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5">
                <Zap className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-[11px] text-gray-300 leading-relaxed">
                  <span className="font-semibold text-amber-300">For lowest latency:</span> set your encoder's <span className="font-medium text-white">Keyframe Interval (GOP)</span> to <span className="font-mono text-white">1–2 seconds</span>.
                  <span className="text-gray-400"> OBS: Settings → Output → Streaming → Keyframe Interval = 1. ASCEND: GOP = 1s.</span> A shorter keyframe interval lets us cut smaller video chunks, so viewers stay closer to live.
                </p>
              </div>

              {planLimits && (
                <div data-testid="plan-encoder-cap" className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5">
                  <Signal className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-gray-300 leading-relaxed">
                    <span className="font-semibold text-amber-300">Your {planLimits.plan_name} plan</span> supports streaming up to
                    <span className="font-medium text-white"> {planLimits.encoder.resolution_label}</span> at
                    <span className="font-mono text-white"> {planLimits.encoder.bitrate_label}</span>.
                    <span className="text-gray-400"> Set your encoder's video bitrate at or below this for the most reliable broadcast.</span>
                    {planLimits.plan !== "ascend-ultra" && (
                      <span className="text-gray-400"> Need 4K at 8 Mbps? Upgrade to Ascend Ultra.</span>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-white flex items-center gap-2"><ServerCog className="h-4 w-4 text-amber-400" /> nginx-rtmp config</p>
                <button data-testid="copy-nginx-config" onClick={() => copy(nginxConfig, "nginx config")} className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition-colors"><Copy className="h-3.5 w-3.5" /> Copy</button>
              </div>
              <pre data-testid="nginx-config-block" className="bg-gray-950 border border-gray-800 rounded-lg p-4 text-[11px] leading-relaxed font-mono text-gray-300 overflow-x-auto whitespace-pre">{nginxConfig}</pre>
              <p className="text-xs text-gray-500 mt-2 flex items-start gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" /> The <span className="text-amber-400 font-mono">on_publish</span> / <span className="text-amber-400 font-mono">on_publish_done</span> hooks post the stream key to StreamWorship, flipping this stream Active/Offline automatically.</p>
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => setEncoderOpen(false)} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg cursor-pointer">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Website live embed code */}
      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent className="bg-card border-gray-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2"><Code2 className="h-5 w-5 text-amber-400" /> Embed this live stream</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add the player to your website. Everyone who watches here is counted toward your live viewer total on the dashboard.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Player width</Label>
              <div className="flex gap-2">
                {[["640", "640×360"], ["960", "960×540"], ["full", "Full width"]].map(([v, lbl]) => (
                  <button key={v} data-testid={`live-embed-size-${v}`} onClick={() => setEmbedSize(v)}
                    className={"px-3 h-9 rounded-lg text-sm border " + (embedSize === v ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-card text-gray-400 border-gray-800 hover:text-white")}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            {/* Top bar color — match the website design */}
            <div className="space-y-2">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-gray-300 text-sm">Match top bar to my website color</span>
                <input data-testid="customize-bar-toggle" type="checkbox" checked={customBar} onChange={(e) => setCustomBar(e.target.checked)} className="h-4 w-4 accent-amber-500 cursor-pointer" />
              </label>
              {customBar && (
                <div className="flex flex-wrap items-center gap-5 rounded-lg bg-gray-950 border border-gray-800 p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Bar color</span>
                    <input data-testid="embed-bar-color" type="color" value={barColor} onChange={(e) => setBarColor(e.target.value)} className="h-9 w-12 rounded-lg bg-transparent border border-gray-800 cursor-pointer" />
                    <span className="text-xs text-gray-500 font-mono">{barColor}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Text</span>
                    <input data-testid="embed-bar-text-color" type="color" value={barTextColor} onChange={(e) => setBarTextColor(e.target.value)} className="h-9 w-12 rounded-lg bg-transparent border border-gray-800 cursor-pointer" />
                    <span className="text-xs text-gray-500 font-mono">{barTextColor}</span>
                  </div>
                  <div className="rounded-md px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5" style={{ background: barColor, color: barTextColor }} data-testid="embed-bar-preview">
                    <Radio className="h-3 w-3" /> {stream.title}
                  </div>
                </div>
              )}
            </div>

            {stream.chat_enabled && (
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-gray-300 text-sm">Show viewer chat in this embed</span>
                <input data-testid="embed-chat-toggle" type="checkbox" checked={embedChat} onChange={(e) => setEmbedChat(e.target.checked)} className="h-4 w-4 accent-amber-500 cursor-pointer" />
              </label>
            )}

            <div className="rounded-lg bg-gray-950 border border-gray-800 p-3">
              <code data-testid="live-embed-code" className="block text-xs text-gray-300 break-all font-mono whitespace-pre-wrap">{embedCode}</code>
            </div>

            <div className="flex items-center justify-between">
              <a data-testid="live-embed-preview" href={embedSrc} target="_blank" rel="noreferrer" className="text-sm text-amber-400 hover:underline inline-flex items-center gap-1.5">
                <ExternalLink className="h-4 w-4" /> Preview player
              </a>
              <button data-testid="copy-live-embed" onClick={() => copy(embedCode, "Embed code")} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2">
                <Copy className="h-4 w-4" /> Copy embed code
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
